import { useState } from 'react';
import { Alert, Button, Card, Image, Spinner } from 'react-bootstrap';
import Lightbox from 'react-18-image-lightbox';
import 'react-18-image-lightbox/style.css';
import { getIndexerErrorInfo } from '../../api/errors';
import {
  useCreateProjectMutation,
  usePatchOnboardingMutation,
} from '../../api/indexerApi';
import indexerApi from '../../api/indexerApi';
import type { OnboardingStatusData } from '../../api/types';
import { projectApiRef } from '../../lib/project-api-ref';
import { useProject } from '../../lib/project-context';
import { siteUrlFromDomainName } from '../../lib/panel-user';
import { useDispatch } from 'react-redux';

const SLIDES = [
  {
    title: 'نمای کلی و KPIها',
    image: 'https://static.mizfa.tools/img/preview/indexer/11.webp',
  },
  {
    title: 'درخواست ایندکس',
    image: 'https://static.mizfa.tools/img/preview/indexer/22.webp',
  },
  {
    title: 'وضعیت ایندکس',
    image: 'https://static.mizfa.tools/img/preview/indexer/33.webp',
  },
  {
    title: 'اطلاع‌رسانی و زمان‌بندی',
    image: 'https://static.mizfa.tools/img/preview/indexer/44.webp',
  },
];

const GALLERY_IMAGES = SLIDES.map(slide => slide.image);

export function OnboardingIntroCarousel({
  onContinue,
}: {
  onContinue: (status: OnboardingStatusData) => void | Promise<void>;
}) {
  const { project, applyProject, domainName, domainSlug } = useProject();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [createProject] = useCreateProjectMutation();
  const [patchOnboarding] = usePatchOnboardingMutation();

  async function handleStart() {
    setError(null);
    setBusy(true);
    try {
      if (!domainName || !domainSlug) {
        throw new Error('دامنه‌ای انتخاب نشده است');
      }

      let active = project;
      if (!active) {
        const siteUrl = siteUrlFromDomainName(domainName);
        active = await createProject({
          name: domainName,
          slug: domainSlug,
          siteUrl,
          indexNowDomain: siteUrl?.replace(/\/$/, ''),
        }).unwrap();
      }

      const ref = projectApiRef(active, domainSlug);
      const nextStatus = await patchOnboarding({
        projectId: ref,
        step: 2,
      }).unwrap();

      // بعد از PATCH: کش slug + state محلی
      dispatch(
        indexerApi.util.upsertQueryData(
          'getProjectBySlug',
          domainSlug,
          { ...active, onboardingStep: 2 },
        ),
      );
      applyProject({ ...active, onboardingStep: 2 });
      await onContinue(nextStatus);
    } catch (err) {
      setError(
        getIndexerErrorInfo(err).message ||
          'ساخت پروژه یا شروع راه‌اندازی ناموفق بود',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-3 border">
      <Card.Header className="bg-body-tertiary">
        <h2 className="h5 mb-3">راه‌اندازی ابزار ایندکسر</h2>
        <p className="text-body mb-0 fs--1">
          دامنه انتخاب‌شده:{' '}
          <span className="mono text-body">{domainName || '—'}</span>
        </p>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <p className="text-body mb-2 fw-semibold fs--1">
          با کمک این ابزار می‌توانید:
        </p>
        <ul className="text-body mb-3 ps-3 d-flex flex-column gap-2 fs--1">
          <li>
            🚀 در حجم بالا درخواست ایندکس ارسال کنید و فرآیند Request Indexing را
            سریع‌تر و ساده‌تر انجام دهید.
          </li>
          <li>
            🔍 وضعیت ایندکس URLها را به‌صورت انبوه بررسی کنید و URLهای
            ایندکس‌نشده، خطاها و مشکلات هر آدرس را شناسایی کنید.
          </li>
          <li>
            ⏰ فرآیند درخواست ایندکس را زمان‌بندی و خودکار کنید تا بدون نیاز به
            انجام دستی، در زمان‌های مشخص اجرا شود.
          </li>
          <li>
            📊 گزارش‌های کاربردی و دقیق دریافت کنید تا یک متخصص حرفه‌ای SEO
            بتواند سریع‌تر مشکلات را شناسایی و مدیریت کند.
          </li>
        </ul>
        <p className="text-body mb-4 fs--1">
          هدف این ابزار ساده است: صرفه‌جویی در زمان متخصصان SEO و کمک به مدیریت
          تعداد زیادی URL با کمترین کار دستی؛ تا زمان بیشتری برای کارهای مهم‌تر و
          در نهایت کسب درآمد بیشتر داشته باشند.
        </p>

        <p className="text-body fw-semibold mb-3 fs--1">پیش‌نمایشی از ابزار:</p>

        <div className="row g-4">
          {SLIDES.map((slide, index) => (
            <div key={slide.title} className="col-6">
              <button
                type="button"
                className="btn btn-link text-decoration-none text-body p-0 w-100"
                onClick={() => setLightboxIndex(index)}
                title={slide.title}
              >
                <Image
                  src={slide.image}
                  alt={slide.title}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  className="border rounded cursor-pointer w-100 object-fit-cover"
                  style={{ aspectRatio: '16 / 9', objectPosition: 'top center' }}
                />
                <div className="small text-center mt-2 px-1 fw-semi-bold">
                  {slide.title}
                </div>
              </button>
            </div>
          ))}
        </div>

        {lightboxIndex !== null && (
          <Lightbox
            mainSrc={GALLERY_IMAGES[lightboxIndex]}
            nextSrc={
              GALLERY_IMAGES[(lightboxIndex + 1) % GALLERY_IMAGES.length]
            }
            prevSrc={
              GALLERY_IMAGES[
                (lightboxIndex - 1 + GALLERY_IMAGES.length) %
                  GALLERY_IMAGES.length
              ]
            }
            onCloseRequest={() => setLightboxIndex(null)}
            onMovePrevRequest={() =>
              setLightboxIndex(
                (lightboxIndex - 1 + GALLERY_IMAGES.length) %
                  GALLERY_IMAGES.length,
              )
            }
            onMoveNextRequest={() =>
              setLightboxIndex((lightboxIndex + 1) % GALLERY_IMAGES.length)
            }
            imageTitle={SLIDES[lightboxIndex]?.title}
            reactModalStyle={{ overlay: { zIndex: 999999 } }}
            onImageLoad={() => {
              window.dispatchEvent(new Event('resize'));
            }}
          />
        )}

      </Card.Body>
      <Card.Footer className="bg-body-tertiary border-top d-flex justify-content-end py-3">
        <Button
          variant="falcon-primary"
          disabled={busy || !domainName || !domainSlug}
          onClick={() => {
            void handleStart();
          }}
        >
          {busy ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                className="me-2"
                style={{ width: 12, height: 12 }}
              />
              در حال ساخت پروژه…
            </>
          ) : (
            'شروع راه‌اندازی'
          )}
        </Button>
      </Card.Footer>
    </Card>
  );
}
